import { useEffect, useState } from 'react';
import { useNotificationStore } from '../stores/notificationStore';

interface UseNotificationsOptions {
  initialCount?: number;
  autoMarkAsRead?: boolean;
  markAsReadDelay?: number;
}

interface UseNotificationsReturn {
  notifications: ReturnType<typeof useNotificationStore.getState>['notifications'];
  loading: ReturnType<typeof useNotificationStore.getState>['loading'];
  hasMore: ReturnType<typeof useNotificationStore.getState>['hasMore'];
  unreadCount: ReturnType<typeof useNotificationStore.getState>['unreadCount'];
  error: ReturnType<typeof useNotificationStore.getState>['error'];
  fetchNotifications: ReturnType<typeof useNotificationStore.getState>['fetchNotifications'];
  fetchMore: ReturnType<typeof useNotificationStore.getState>['fetchMore'];
  markNotificationsAsRead: ReturnType<
    typeof useNotificationStore.getState
  >['markNotificationsAsRead'];
  previousUnreadCount: number;
}

const DEFAULT_MARK_AS_READ_DELAY = 3000; // 3 seconds

export const useNotifications = (options: UseNotificationsOptions = {}): UseNotificationsReturn => {
  const {
    initialCount = 10,
    autoMarkAsRead = true,
    markAsReadDelay = DEFAULT_MARK_AS_READ_DELAY,
  } = options;

  const {
    notifications,
    loading,
    hasMore,
    unreadCount,
    fetchNotifications,
    fetchMore,
    error,
    markNotificationsAsRead,
    fetchEmailOnlyTypes,
    emailOnlyTypesLoaded,
  } = useNotificationStore();

  const [previousUnreadCount, setPreviousUnreadCount] = useState(unreadCount);

  // Fetch email-only types once on mount (must complete before fetching notifications)
  useEffect(() => {
    if (!emailOnlyTypesLoaded) {
      fetchEmailOnlyTypes();
    }
  }, [emailOnlyTypesLoaded, fetchEmailOnlyTypes]);

  // Initial fetch — gated on email-only types being loaded to avoid fetching
  // email-only notifications before the filter is available
  useEffect(() => {
    if (!emailOnlyTypesLoaded) return;
    if (notifications.length < initialCount) {
      fetchNotifications(initialCount);
    }
  }, [initialCount, notifications.length, fetchNotifications, emailOnlyTypesLoaded]);

  // Handle unread count changes and refetch if needed
  useEffect(() => {
    if (unreadCount != previousUnreadCount && unreadCount > 0) {
      setPreviousUnreadCount(unreadCount);
      fetchNotifications(notifications.length + unreadCount);
    }
  }, [unreadCount, notifications.length, previousUnreadCount, fetchNotifications]);

  // Auto mark notifications as read
  useEffect(() => {
    if (!autoMarkAsRead || notifications.length === 0) return;

    const unreadIds = notifications.filter(n => !n.read_at).map(n => n.id);
    if (unreadIds.length === 0) return;

    const timeout = setTimeout(() => {
      markNotificationsAsRead(unreadIds);
    }, markAsReadDelay);

    return () => clearTimeout(timeout);
  }, [notifications, autoMarkAsRead, markAsReadDelay, markNotificationsAsRead]);

  return {
    notifications,
    loading,
    hasMore,
    unreadCount,
    error,
    fetchNotifications,
    fetchMore,
    markNotificationsAsRead,
    previousUnreadCount,
  };
};

export default useNotifications;
